'use client';

/**
 * SafeContent Component
 *
 * Safely renders user-generated HTML content with:
 * - DOMPurify sanitization
 * - Automatic XSS detection logging
 * - Hydration-safe implementation
 *
 * @example
 * ```tsx
 * <SafeContent
 *   content={userContent}
 *   onSuspiciousContent={(detected) => {
 *     if (detected) logger.warn('XSS attempt detected');
 *   }}
 * />
 * ```
 */

import { useEffect, useState } from 'react';
import { sanitizeHTML, isSuspiciousContent } from '@/lib/sanitize';

export type SafeContentProps = {
  /** HTML content to render (will be sanitized) */
  content: string;
  /** Optional callback when suspicious content is detected */
  onSuspiciousContent?: (detected: boolean) => void;
  /** Optional CSS class for the wrapper */
  className?: string;
  /** Show loading skeleton during hydration */
  showSkeleton?: boolean;
};

/**
 * SafeContent Component
 * Renders sanitized HTML with automatic XSS prevention
 */
export function SafeContent({
  content,
  onSuspiciousContent,
  className = '',
  showSkeleton = true,
}: SafeContentProps) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);

    // Detect and log suspicious content patterns
    if (isSuspiciousContent(content)) {
      console.warn(
        '[Security] Suspicious content detected - potential XSS attempt'
      );
      onSuspiciousContent?.(true);
    }
  }, [content, onSuspiciousContent]);

  // Show loading skeleton during SSR/hydration mismatch prevention
  if (!isMounted && showSkeleton) {
    return (
      <div className={`min-h-24 bg-gray-200 animate-pulse rounded ${className}`} />
    );
  }

  // Sanitize HTML before rendering
  const cleanHTML = sanitizeHTML(content);

  // If sanitization removed everything, show empty state
  if (!cleanHTML || cleanHTML.trim() === '') {
    return (
      <div className={`text-gray-500 italic ${className}`}>
        No content available
      </div>
    );
  }

  return (
    <div
      className={`prose prose-sm max-w-none dark:prose-invert ${className}`}
      dangerouslySetInnerHTML={{ __html: cleanHTML }}
    />
  );
}

export default SafeContent;
